import * as React from "react";

/** Fetch-and-apply with a request-sequence guard plus a chained-timeout poller.
 *
 *  `refresh()` applies a response only if no newer request was issued since it
 *  started, so a slow poll can't overwrite a fresher manual refresh. While
 *  `active`, the next poll is scheduled only after the previous one settles,
 *  so slow responses never stack. Stops on unmount / when `active` goes false. */
export function useLatestPoll<T>(
  load: () => Promise<T>,
  apply: (value: T) => void,
  onError: (e: unknown) => void,
  active: boolean,
  intervalMs: number,
): () => Promise<void> {
  const seq = React.useRef(0);
  const cbs = React.useRef({ load, apply, onError });
  cbs.current = { load, apply, onError };

  const refresh = React.useCallback(async () => {
    const mine = ++seq.current;
    try {
      const v = await cbs.current.load();
      if (mine === seq.current) cbs.current.apply(v);
    } catch (e) {
      if (mine === seq.current) cbs.current.onError(e);
    }
  }, []);

  React.useEffect(() => {
    if (!active) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      await refresh();
      if (!stopped) timer = setTimeout(tick, intervalMs);
    };
    timer = setTimeout(tick, intervalMs);
    return () => { stopped = true; clearTimeout(timer); };
  }, [active, intervalMs, refresh]);

  // Invalidate in-flight responses on unmount.
  React.useEffect(() => () => { seq.current++; }, []);

  return refresh;
}
